// API Configuration and Service
// In production, use the Cloud Run API URL directly
// In development, use localhost
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:3000/poker-api' : 'https://poker-prod-api-azeg4kcklq-uc.a.run.app/poker-api');

// Helper for making API requests
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error(`API request failed: ${endpoint}`, error);
    throw error;
  }
}

// Games API
export const gamesApi = {
  // Get available games within 2500ft
  getAvailableGames: async (lat, lon, userId = null) => {
    let endpoint = `/games/available?lat=${lat}&lon=${lon}`;
    if (userId) {
      endpoint += `&userId=${userId}`;
    }
    return apiRequest(endpoint);
  },

  // Get seating chart for a specific game
  getGameSeating: async (gameId) => {
    return apiRequest(`/games/${gameId}/seating`);
  },

  // Get games assigned to a dealer
  getDealerGames: async (dealerId) => {
    return apiRequest(`/games/dealer?dealerId=${dealerId}`);
  },

  // Get all games (no filter)
  getAll: async () => {
    return apiRequest('/games');
  },

  // Quick-create a test game with venue and players
  quickCreate: async (data) => {
    return apiRequest('/games/quick-create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get game results (eliminations)
  getGameResults: async (gameId) => {
    return apiRequest(`/games/${gameId}/results`);
  },

  // Record a player elimination
  recordElimination: async (gameId, playerId, finishPosition) => {
    return apiRequest(`/games/${gameId}/results`, {
      method: 'POST',
      body: JSON.stringify({ playerId, finishPosition }),
    });
  },

  // Complete a game
  completeGame: async (gameId) => {
    return apiRequest(`/games/${gameId}/complete`, {
      method: 'PUT',
    });
  },
};

// Seats API
export const seatsApi = {
  // Reserve a seat
  reserveSeat: async (gameId, userId, userLat, userLon, tableId = null, seatNumber = null) => {
    const body = {
      userId,
      userLat,
      userLon,
    };

    if (tableId) body.tableId = tableId;
    if (seatNumber) body.seatNumber = seatNumber;

    return apiRequest(`/games/${gameId}/seats`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Swap/move a player to a different seat (VIP — requires password)
  swapSeat: async (gameId, playerId, password, targetTableId, targetSeatNumber) => {
    const body = {
      playerId,
      targetTableId,
      targetSeatNumber,
    };
    if (password) body.password = password;

    return apiRequest(`/games/${gameId}/seats/swap`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Dealer swap/move a player to a different seat (no password required)
  dealerSwapSeat: async (gameId, userId, targetTableId, targetSeatNumber) => {
    return apiRequest(`/games/${gameId}/seats/dealer-swap`, {
      method: 'POST',
      body: JSON.stringify({ userId, targetTableId, targetSeatNumber }),
    });
  },

  // Remove a player from their seat (dealer action)
  removeSeat: async (gameId, userId) => {
    return apiRequest(`/games/${gameId}/seats/remove`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  },

  // Dealer seat a player (bypasses distance/time checks, finds first available seat)
  dealerSeatPlayer: async (gameId, playerId) => {
    return apiRequest(`/games/${gameId}/seats/dealer-seat`, {
      method: 'POST',
      body: JSON.stringify({ playerId }),
    });
  },
};

// Users API
export const usersApi = {
  // Get user by username
  getUserByUsername: async (username) => {
    return apiRequest(`/users?username=${encodeURIComponent(username)}`);
  },

  // Get user by phone number
  getUserByPhone: async (phone) => {
    return apiRequest(`/users/by-phone?phone=${encodeURIComponent(phone)}`);
  },

  // Login with phone + password
  login: async (phone, password) => {
    return apiRequest('/users/login', {
      method: 'POST',
      body: JSON.stringify({ phone, password }),
    });
  },

  // Get all dealers (for swap targeting)
  getDealers: async () => apiRequest('/users/dealers'),

  // Change own password
  changePassword: async (userId, currentPassword, newPassword) => apiRequest(`/users/${userId}/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  }),
};

// Waiting List API
export const waitingListApi = {
  // Join waiting list
  joinWaitingList: async (gameId, userId, userLat, userLon) => {
    return apiRequest(`/games/${gameId}/waiting-list`, {
      method: 'POST',
      body: JSON.stringify({
        userId,
        userLat,
        userLon,
      }),
    });
  },

  // Get waiting list
  getWaitingList: async (gameId) => {
    return apiRequest(`/games/${gameId}/waiting-list`);
  },
};

// Blinds API
export const blindsApi = {
  // Get blind timer status for a game
  getBlindTimer: async (gameId) => {
    return apiRequest(`/games/${gameId}/blinds`);
  },

  // Start the blind timer
  startTimer: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/blinds/start`, {
      method: 'POST',
      body: JSON.stringify({ dealerId }),
    });
  },

  // Pause the blind timer
  pauseTimer: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/blinds/pause`, {
      method: 'POST',
      body: JSON.stringify({ dealerId }),
    });
  },

  // Resume the blind timer
  resumeTimer: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/blinds/resume`, {
      method: 'POST',
      body: JSON.stringify({ dealerId }),
    });
  },

  // Advance to next blind level
  advanceLevel: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/blinds/advance`, {
      method: 'POST',
      body: JSON.stringify({ dealerId }),
    });
  },

  // Stop the blind timer (end game)
  stopTimer: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/blinds/stop`, {
      method: 'POST',
      body: JSON.stringify({ dealerId }),
    });
  },
};

// Dealer shift management API (Seven Deuce integration)
export const dealerShiftsApi = {
  getShifts: async (dealerId) => apiRequest(`/dealers/${dealerId}/shifts`),

  confirm: async (dealerId, gameId, status) => apiRequest(`/dealers/${dealerId}/shifts/${gameId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  }),

  checkIn: async (dealerId, gameId, latitude, longitude) => apiRequest(`/dealers/${dealerId}/shifts/${gameId}/checkin`, {
    method: 'POST',
    body: JSON.stringify({ latitude, longitude }),
  }),

  getEarnings: async (dealerId) => apiRequest(`/dealers/${dealerId}/earnings`),

  getMessages: async (dealerId) => apiRequest(`/dealers/${dealerId}/messages`),

  markRead: async (dealerId, messageId) => apiRequest(`/dealers/${dealerId}/messages/${messageId}/read`, {
    method: 'POST',
  }),

  // Shift swaps
  requestSwap: async (dealerId, gameId, toDealerId, note) => apiRequest(`/dealers/${dealerId}/swap`, {
    method: 'POST',
    body: JSON.stringify({ gameId, toDealerId, note }),
  }),

  respondSwap: async (dealerId, swapId, status) => apiRequest(`/dealers/${dealerId}/swap/${swapId}/respond`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  }),

  getSwaps: async (dealerId) => apiRequest(`/dealers/${dealerId}/swaps`),

  // Availability
  setAvailability: async (dealerId, date, available, note) => apiRequest(`/dealers/${dealerId}/availability`, {
    method: 'POST',
    body: JSON.stringify({ date, available, note }),
  }),

  getAvailability: async (dealerId) => apiRequest(`/dealers/${dealerId}/availability`),

  // Notification prefs
  getPrefs: async (userId) => apiRequest(`/users/${userId}/notification-prefs`),

  setPrefs: async (userId, prefs) => apiRequest(`/users/${userId}/notification-prefs`, {
    method: 'PUT',
    body: JSON.stringify(prefs),
  }),
};

// ===== Notifications v2 API =====
export const notificationsApi = {
  star: (id) => apiRequest(`/messages/${id}/star`, { method: 'POST' }),
  mute: (id) => apiRequest(`/messages/${id}/mute`, { method: 'POST' }),
  markAllRead: (userId) => apiRequest(`/messages/mark-all-read/${userId}`, { method: 'POST' }),
  search: (userId, q) => apiRequest(`/messages/search/${userId}?q=${encodeURIComponent(q)}`),
  snooze: (userId, hours) => apiRequest(`/notifications/snooze/${userId}`, { method: 'POST', body: JSON.stringify({ hours }) }),
  ack: (logId) => apiRequest(`/notifications/ack/${logId}`, { method: 'POST' }),
  getEventPrefs: (userId) => apiRequest(`/notifications/event-prefs/${userId}`),
  setEventPref: (userId, eventType, channels) => apiRequest(`/notifications/event-prefs/${userId}`, { method: 'PUT', body: JSON.stringify({ eventType, channels }) }),
};

// ===== Dealer Hub Upgrades API =====
export const dealerHubApi = {
  // Availability
  blockDate: (dealerId, date, note) => apiRequest(`/dealers/${dealerId}/availability`, { method: 'POST', body: JSON.stringify({ date, note }) }),
  unblockDate: (dealerId, blockId) => apiRequest(`/dealers/${dealerId}/availability/${blockId}`, { method: 'DELETE' }),
  // Hand notes
  getHandNotes: (gameId) => apiRequest(`/games/${gameId}/hand-notes`),
  addHandNote: (gameId, dealerId, note, playerId, tag) => apiRequest(`/games/${gameId}/hand-notes`, { method: 'POST', body: JSON.stringify({ dealerId, note, playerId, tag }) }),
  // Player flags
  getPlayerFlags: (playerId) => apiRequest(`/players/${playerId}/flags`),
  addPlayerFlag: (playerId, flag, setBy) => apiRequest(`/players/${playerId}/flags`, { method: 'POST', body: JSON.stringify({ flag, setBy }) }),
  // Incidents
  reportIncident: (dealerId, gameId, type, description, severity) => apiRequest(`/dealer-incidents`, { method: 'POST', body: JSON.stringify({ dealerId, gameId, type, description, severity }) }),
  // End-of-shift
  submitEndOfShift: (gameId, dealerId, payload) => apiRequest(`/games/${gameId}/end-of-shift`, { method: 'POST', body: JSON.stringify({ dealerId, ...payload }) }),
  // Open shifts
  listOpenShifts: () => apiRequest(`/open-shifts`),
  postOpenShift: (gameId, tableNumber, postedBy) => apiRequest(`/open-shifts`, { method: 'POST', body: JSON.stringify({ gameId, tableNumber, postedBy }) }),
  claimOpenShift: (id, dealerId) => apiRequest(`/open-shifts/${id}/claim`, { method: 'POST', body: JSON.stringify({ dealerId }) }),
  // Threads (admin chat)
  getThreads: (dealerId) => apiRequest(`/dealers/${dealerId}/threads`),
  createThread: (dealerId, subject) => apiRequest(`/dealers/${dealerId}/threads`, { method: 'POST', body: JSON.stringify({ subject }) }),
  getThreadMessages: (threadId) => apiRequest(`/threads/${threadId}/messages`),
  postThreadMessage: (threadId, senderId, body) => apiRequest(`/threads/${threadId}/messages`, { method: 'POST', body: JSON.stringify({ senderId, body }) }),
  markThreadRead: (threadId, dealerId) => apiRequest(`/threads/${threadId}/read`, { method: 'POST', body: JSON.stringify({ dealerId }) }),
  // Stats
  getStats: (dealerId) => apiRequest(`/dealers/${dealerId}/stats`),
};

const api = {
  games: gamesApi,
  seats: seatsApi,
  users: usersApi,
  waitingList: waitingListApi,
  blinds: blindsApi,
  dealerShifts: dealerShiftsApi,
  dealerHub: dealerHubApi,
};

export default api;
